import { getNation } from '@brig/nations';
import { flagArt } from '@brig/nations/flags';
import { palette } from '@brig/theme/tokens';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n/index.ts';
import { flutterPath } from './flutter.ts';

export interface NationFlagProps {
  nation: string;
  /** Height in CSS px; the width follows the flag's proportions. */
  height: number;
  /** A light flutter: wavy top and bottom edges and soft folds. */
  waving?: boolean;
  /** Flying to the left, hoist on the right (seen from the other side). */
  reversed?: boolean;
  /** Position and size in the parent's user units when nested inside another <svg>. */
  x?: number;
  y?: number;
  /** Height in the parent's user units when nested (defaults to `height`). */
  box?: number;
  /** Decorative next to a visible name: hide from assistive tech. */
  decorative?: boolean;
  className?: string;
}

export function NationFlag({
  nation,
  height,
  waving = false,
  reversed = false,
  x,
  y,
  box,
  decorative = false,
  className,
}: NationFlagProps) {
  useTranslation(); // re-render on language change for the label
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const art = flagArt(nation);
  const { width: w, height: h } = art;
  const outline = waving ? flutterPath(w, h) : `M0 0H${w}V${h}H0Z`;
  // The viewBox leaves room for the flutter and the outline; (x, y) stays the top of the hoist.
  const scale = (box ?? height) / h;
  const padX = 0.5;
  const padY = 0.5 + h * 0.08;
  const label = getNation(nation).names[currentLocale()];
  return (
    <svg
      x={x === undefined ? undefined : x - padX * scale}
      y={y === undefined ? undefined : y - padY * scale}
      width={(w + 2 * padX) * scale}
      height={(h + 2 * padY) * scale}
      viewBox={`${-padX} ${-padY} ${w + 2 * padX} ${h + 2 * padY}`}
      overflow="visible"
      className={className}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
      data-nation={nation}
    >
      <defs>
        <clipPath id={`clip${id}`}>
          <path d={outline} />
        </clipPath>
        {waving ? (
          <linearGradient id={`folds${id}`} x1="0" x2="1" y1="0" y2="0">
            {[0, 0.17, 0.33, 0.5, 0.67, 0.83, 1].map((o, i) => (
              <stop key={o} offset={o} stopColor={i % 2 ? '#000' : '#fff'} stopOpacity={i % 2 ? 0.16 : 0.1} />
            ))}
          </linearGradient>
        ) : null}
      </defs>
      <g transform={reversed ? `translate(${w} 0) scale(-1 1)` : undefined}>
        <g clipPath={`url(#clip${id})`}>
          <g dangerouslySetInnerHTML={{ __html: art.body }} />
          {waving ? <rect width={w} height={h * 1.2} y={-h * 0.1} fill={`url(#folds${id})`} /> : null}
        </g>
        <path
          d={outline}
          fill="none"
          stroke={palette.ink}
          strokeOpacity={0.75}
          strokeWidth={0.8}
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </svg>
  );
}
